import { userActions } from '@/actions';
import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { EVENT_NAME, SCREEN_VIEW } from '@/constants/analytics';
import { getCookie, isIPhone, promiseWrapper, removeCookie, trackMpEvent } from '@/helpers';
import { openIosKeyboard } from '@/helpers/utilsTS';
import { _s } from '@/locale';
import { loginServices } from '@/services';
import { TriggerSource } from '@/types/analytics';
import { ReactNode, createContext, useEffect, useReducer, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import isEmail from 'validator/lib/isEmail';

type EmailState = {
  value: string;
  error: boolean;
  submitting: boolean;
  isChecked: boolean;
  isRegistered: boolean;
};

type EmailAction =
  | { type: 'SET_EMAIL_VALUE'; payload: { value: string } }
  | { type: 'SET_EMAIL_ERROR'; payload: { error: boolean } }
  | { type: 'SET_EMAIL_SUBMITTING'; payload: { submitting: boolean } }
  | { type: 'SET_EMAIL_IS_MP_CHECKED'; payload: { isChecked: boolean } }
  | { type: 'SET_EMAIL_IS_REGISTERED'; payload: { isRegistered: boolean } };

const emailReducer = (state: EmailState, action: EmailAction): EmailState => {
  switch (action.type) {
    case 'SET_EMAIL_VALUE': {
      const { value } = action.payload;
      return { ...state, value };
    }
    case 'SET_EMAIL_ERROR': {
      const { error } = action.payload;
      return { ...state, error };
    }
    case 'SET_EMAIL_SUBMITTING': {
      const { submitting } = action.payload;
      return { ...state, submitting };
    }
    case 'SET_EMAIL_IS_MP_CHECKED': {
      const { isChecked } = action.payload;
      return { ...state, isChecked };
    }
    case 'SET_EMAIL_IS_REGISTERED': {
      const { isRegistered } = action.payload;
      return { ...state, isRegistered };
    }
  }
};

export type View = 'requestToken' | 'verifyOtp' | 'verifySuccess' | 'createAccount';

async function requestOTPCode(email: string, triggerSource: TriggerSource, redirectUrl: string) {
  await loginServices.requestOtp({
    email,
    triggerSource,
    redirectUrl,
  });
}

const useLoginManager = (): {
  email: EmailState;
  show: boolean;
  view: View;
  user: any;
  updateView: (view: View) => void;
  handleEmailSubmit: ({ triggerSource, redirectUrl }: { triggerSource?: TriggerSource; redirectUrl: string }) => void;
  handleLogin: (code: string[]) => Promise<boolean>;
  updateEmail: (email: string) => void;
  onOpen: () => void;
  onClose: () => void;
  clearLoginState: () => void;
  handleEmailResendClicked: (redirectUrl: string, triggerSource?: TriggerSource) => void;
} => {
  const [email, dispatch] = useReducer(emailReducer, {
    value: '',
    error: false,
    submitting: false,
    isChecked: false,
    isRegistered: false,
  });
  const [show, setShow] = useState(false);
  const [view, setView] = useState<View>('requestToken');
  const user = useSelector((state: any) => state.users)?.user;
  const storeDispatch = useDispatch();

  const handleEmailSubmit = async ({
    triggerSource,
    redirectUrl,
  }: {
    triggerSource: TriggerSource;
    redirectUrl: string;
  }) => {
    dispatch({ type: 'SET_EMAIL_SUBMITTING', payload: { submitting: true } });
    if (email.error) {
      trackMpEvent(EVENT_NAME.LOGIN_FAILED, { screen_name: SCREEN_VIEW.LOGIN_START, initiated_from: triggerSource });
      return;
    }

    if (!email.isChecked) {
      const isRegistered = await checkIsEmailRegistered();
      if (!isRegistered) {
        trackMpEvent(EVENT_NAME.SIGN_UP_INITIATED, {
          screen_name: SCREEN_VIEW.LOGIN_START,
          initiated_from: triggerSource,
        });
        setView('createAccount');
        return;
      } else {
        trackMpEvent(EVENT_NAME.LOGIN_INITIATED, {
          screen_name: SCREEN_VIEW.LOGIN_START,
          initiated_from: triggerSource,
        });
      }
    }

    if (isIPhone()) {
      openIosKeyboard();
    }

    requestOTPCode(email.value, triggerSource, redirectUrl).catch(() => {
      toast(
        ({ closeToast }) => (
          <Snackbar
            type="danger"
            label={_s('loginModal.error.message')}
            action={
              <button
                onClick={() => {
                  closeToast();
                  setView('requestToken');
                }}>
                {_s('loginModal.error.cta')}
              </button>
            }
          />
        ),
        { autoClose: false },
      );
    });

    setView('verifyOtp');
  };

  const checkIsEmailRegistered = async () => {
    const { error } = await promiseWrapper(loginServices.isEmailRegistered(email.value));

    dispatch({ type: 'SET_EMAIL_IS_MP_CHECKED', payload: { isChecked: true } });
    dispatch({ type: 'SET_EMAIL_IS_REGISTERED', payload: { isRegistered: !error } });

    if (error) {
      dispatch({ type: 'SET_EMAIL_SUBMITTING', payload: { submitting: false } });
      return false;
    }

    return true;
  };

  const handleEmailResendClicked = (redirectUrl: string, triggerSource: TriggerSource) => {
    const amplitudeTracking = async () => {
      if (email.isChecked && !email.isRegistered) {
        trackMpEvent(EVENT_NAME.SIGN_UP_RESUBMIT_CODE, {
          screen_name: SCREEN_VIEW.LOGIN_START,
          initiated_from: triggerSource,
        });
      } else {
        trackMpEvent(EVENT_NAME.LOGIN_RESUBMIT_CODE, {
          screen_name: SCREEN_VIEW.LOGIN_START,
          initiated_from: triggerSource,
        });
      }
    };

    amplitudeTracking();

    requestOTPCode(email.value, SCREEN_VIEW.OTP_LOGIN_VERIFY, redirectUrl).catch(() => {
      toast(
        ({ closeToast }) => (
          <Snackbar
            type="danger"
            label={_s('loginModal.error.message')}
            action={
              <button
                onClick={() => {
                  closeToast();
                  setView('requestToken');
                }}>
                {_s('loginModal.error.cta')}
              </button>
            }
          />
        ),
        { autoClose: false },
      );
    });
  };

  const handleLogin = async (code: string[]): Promise<boolean> => {
    const { error } = await promiseWrapper(loginServices.login({ email: email.value, code: code.join('') }));
    const token = getCookie('BokadirektMP');
    if (error || !token) return false;
    removeCookie('BokadirektMP');

    // @ts-ignore
    storeDispatch(userActions.profile(token, true, false));
    return true;
  };

  const updateEmail = (_email: string) => {
    dispatch({ type: 'SET_EMAIL_VALUE', payload: { value: _email } });
    dispatch({ type: 'SET_EMAIL_SUBMITTING', payload: { submitting: false } });
    dispatch({ type: 'SET_EMAIL_IS_MP_CHECKED', payload: { isChecked: false } });
  };

  const updateView = (view: View) => {
    if (view === 'verifyOtp' && email.error) setView('requestToken');
    setView(view);
  };

  const clearLoginState = () => {
    dispatch({ type: 'SET_EMAIL_VALUE', payload: { value: '' } });
    dispatch({ type: 'SET_EMAIL_ERROR', payload: { error: false } });
    dispatch({ type: 'SET_EMAIL_SUBMITTING', payload: { submitting: false } });
    dispatch({ type: 'SET_EMAIL_IS_MP_CHECKED', payload: { isChecked: false } });
    setView('requestToken');
  };

  useEffect(() => {
    dispatch({ type: 'SET_EMAIL_ERROR', payload: { error: !isEmail(email.value) } });
  }, [email.value]);

  return {
    show,
    email,
    view,
    user,
    updateEmail,
    updateView,
    handleEmailSubmit,
    handleLogin,
    onOpen: () => setShow(true),
    onClose: () => setShow(false),
    clearLoginState,
    handleEmailResendClicked,
  };
};

export type LoginContextType = ReturnType<typeof useLoginManager>;

export const LoginContext = createContext<LoginContextType>({
  show: false,
  email: {
    value: '',
    error: false,
    submitting: false,
    isChecked: false,
    isRegistered: false,
  },
  view: 'requestToken',
  onOpen: () => {},
  onClose: () => {},
  updateView: () => {},
  handleEmailSubmit: () => {},
  handleLogin: () => Promise.resolve(false),
  updateEmail: () => {},
  clearLoginState: () => {},
  handleEmailResendClicked: () => {},
  user: {},
});

export const LoginProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return <LoginContext.Provider value={useLoginManager()}>{children}</LoginContext.Provider>;
};
